import { useEffect, useRef, useState } from "react";
import {
  DropDownItem,
  DropDownMenu,
  Button,
} from "@monolith-forensics/monolith-ui";
import styled from "styled-components";

interface SimpleCameraProps {
  onCapture?: (photoUrls: string[]) => void;
}

const CameraContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CameraVideo = styled.video`
  width: 100%;
  max-width: 50vw;
  border-radius: 4px;
`;

const CameraControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ThumbnailGrid = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 10px 0;
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const DeleteButton = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${({ theme }) => theme.palette.error.main};
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: ${({ theme }) => theme.palette.error.dark};
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  align-items: center;
`;

const SimpleCamera = ({
  onCapture,
  onClose,
}: SimpleCameraProps & { onClose: () => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>();
  const [error, setError] = useState<string>("");
  const [isReady, setIsReady] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState<
    { url: string; deviceId: string; timestamp: number }[]
  >([]);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera API is not supported in this browser");
      return;
    }

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: selectedDevice
            ? { deviceId: selectedDevice }
            : { facingMode: "environment" },
        });

        if (videoElement) {
          videoElement.srcObject = stream;
          videoElement.onloadedmetadata = () => setIsReady(true);
        }

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        setDevices(videoDevices);
        if (!selectedDevice && videoDevices.length) {
          setSelectedDevice(videoDevices[0].deviceId);
        }
      } catch (err: unknown) {
        setError(`Camera error: ${(err as Error).message}`);
      }
    };

    startCamera();

    return () => {
      const stream = videoElement?.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [selectedDevice]);

  const takePhoto = () => {
    if (videoRef.current && isReady) {
      const canvas = document.createElement("canvas");
      const video = videoRef.current;

      // Store camera info with photo
      const track = (video.srcObject as MediaStream)?.getVideoTracks()[0];

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(video, 0, 0);

      setCapturedPhotos((prev) => [
        ...prev,
        {
          url: canvas.toDataURL("image/jpeg"),
          deviceId: track?.getSettings().deviceId || "unknown",
          timestamp: Date.now(),
        },
      ]);
    }
  };

  return (
    <div>
      {error ? (
        <div>Error: {error}</div>
      ) : (
        <CameraContainer>
          <CameraVideo ref={videoRef} autoPlay playsInline />

          <CameraControls>
            <span>Camera:</span>
            <DropDownMenu
              arrow={true}
              data={devices.map((device) => ({
                label: device.label || `Camera ${devices.indexOf(device) + 1}`,
                value: device.deviceId,
              }))}
              onItemSelect={(selected: DropDownItem) => {
                setSelectedDevice(selected.value as string);
              }}
              dropDownProps={{
                style: { width: 250, maxWidth: 400 },
              }}
            >
              {devices.find((d) => d.deviceId === selectedDevice)?.label ||
                `Camera ${
                  devices.findIndex((d) => d.deviceId === selectedDevice) + 1
                }`}
            </DropDownMenu>
          </CameraControls>

          {capturedPhotos.length > 0 && (
            <ThumbnailGrid>
              {capturedPhotos.map((photo, index) => (
                <ThumbnailContainer key={index}>
                  <ThumbnailImage
                    src={photo.url}
                    alt={`Captured ${index + 1}`}
                  />
                  <DeleteButton
                    onClick={() => {
                      setCapturedPhotos((prev) =>
                        prev.filter((_, i) => i !== index)
                      );
                    }}
                  >
                    ×
                  </DeleteButton>
                </ThumbnailContainer>
              ))}
            </ThumbnailGrid>
          )}

          <ActionBar>
            <Button size="xxs" variant="text" color="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="xxs"
              onClick={takePhoto}
              disabled={!isReady}
            >
              Take Photo
            </Button>
            {capturedPhotos.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="xxs"
                onClick={() => {
                  onCapture?.(capturedPhotos.map((photo) => photo.url));
                }}
              >
                Upload {capturedPhotos.length} Photo
                {capturedPhotos.length > 1 ? "s" : ""}
              </Button>
            )}
          </ActionBar>
        </CameraContainer>
      )}
    </div>
  );
};

export default SimpleCamera;
